import { useState, useEffect } from 'react';
import { ProyectoMock } from '../../../mocks/proyectos';
import { useDocumentosDB } from '../../../hooks/useDocumentosDB';

interface Props {
  proyecto: ProyectoMock;
}

const estadoColor: Record<string, string> = {
  'Pendiente evaluación': 'bg-amber-100 text-amber-700 border-amber-200',
  'En evaluación': 'bg-sky-100 text-sky-700 border-sky-200',
  'Con correcciones': 'bg-orange-100 text-orange-700 border-orange-200',
  'Aprobado': 'bg-green-100 text-green-700 border-green-200',
  'No pertinente': 'bg-red-100 text-red-700 border-red-200',
};

const categoriaColor: Record<string, string> = {
  'Tesis': 'bg-violet-50 text-violet-700 border-violet-200',
  'Proyecto institucional': 'bg-teal-50 text-teal-700 border-teal-200',
  'Proyecto docente': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Convenio': 'bg-rose-50 text-rose-700 border-rose-200',
};

function Field({ label, value }: { label: string; value?: string }) {
  if (!value || value === '—') return null;
  return (
    <div>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-0.5">{label}</p>
      <p className="text-sm text-slate-700 whitespace-pre-wrap">{value}</p>
    </div>
  );
}

export default function ProyectoCardDetalle({ proyecto }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [hasDoc, setHasDoc] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const { hasDocumento, downloadDocumento } = useDocumentosDB();

  useEffect(() => {
    hasDocumento(proyecto.codigo).then(setHasDoc).catch(() => setHasDoc(false));
  }, [proyecto.codigo, hasDocumento]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadDocumento(proyecto.codigo);
    } finally {
      setDownloading(false);
    }
  };

  const estadoCls = estadoColor[proyecto.estado] ?? 'bg-slate-100 text-slate-600 border-slate-200';
  const categoriaCls = categoriaColor[proyecto.categoria] ?? 'bg-slate-100 text-slate-600 border-slate-200';

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 flex flex-col gap-3">
        {/* Top row: code + badges */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded-md px-2.5 py-0.5">
            {proyecto.codigo}
          </span>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${estadoCls}`}>
            {proyecto.estado}
          </span>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${categoriaCls}`}>
            {proyecto.categoria}
          </span>
          {proyecto.etica === 'Sí' && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-purple-50 text-purple-700 border-purple-200">
              Requiere ética
            </span>
          )}
        </div>

        {/* Title */}
        <div>
          <p className="text-sm font-semibold text-slate-800 leading-snug">{proyecto.titulo}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-user-line" /></span>
              {proyecto.investigador}
            </span>
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-flask-line" /></span>
              {proyecto.area}
            </span>
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-calendar-line" /></span>
              Recibido: {proyecto.fechaRecepcion}
            </span>
          </div>
        </div>

        {/* Action row */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            onClick={() => setExpanded(v => !v)}
            className="whitespace-nowrap flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 bg-slate-50 rounded-md px-3 py-1.5 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className={expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
            </span>
            {expanded ? 'Ocultar detalles' : 'Ver detalles'}
          </button>

          {(hasDoc || proyecto.documentoNombre) ? (
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="whitespace-nowrap flex items-center gap-1.5 text-xs font-semibold text-teal-700 border border-teal-300 bg-teal-50 rounded-md px-3 py-1.5 hover:bg-teal-100 transition-colors cursor-pointer disabled:opacity-60"
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className={downloading ? 'ri-loader-4-line animate-spin' : 'ri-download-2-line'} />
              </span>
              {downloading ? 'Descargando...' : (proyecto.documentoNombre ?? 'Documento adjunto')}
            </button>
          ) : (
            <span className="whitespace-nowrap flex items-center gap-1.5 text-xs text-slate-400 border border-slate-200 bg-slate-50 rounded-md px-3 py-1.5">
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-file-unknow-line" /></span>
              Sin documento adjunto
            </span>
          )}

          {proyecto.presupuesto && proyecto.presupuesto !== '—' && (
            <span className="whitespace-nowrap text-xs text-slate-500 ml-auto">
              Presupuesto: <strong>{proyecto.presupuesto}</strong>
            </span>
          )}
        </div>
      </div>

      {/* Expandable detail */}
      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Resumen ejecutivo" value={proyecto.resumen} />
            <Field label="Objetivos" value={proyecto.objetivos} />
            <Field label="Diseño y metodología" value={proyecto.diseno} />
            <Field label="Cronograma" value={proyecto.cronograma} />
            <Field label="Autores del equipo" value={proyecto.equipo} />
            <Field label="Plan de publicación" value={proyecto.plan_publicacion} />
            <Field label="Consideraciones éticas" value={proyecto.necesidad_etica} />
            <Field label="Justificación ética" value={proyecto.justificacion_etica} />
            <Field label="Unidad / Departamento" value={proyecto.unidad} />
            <Field label="Fuente de financiamiento" value={proyecto.financiamiento} />
            <Field label="Fecha estimada de inicio" value={proyecto.fecha_inicio} />
            <Field label="Correo electrónico" value={proyecto.email} />
          </div>
        </div>
      )}
    </div>
  );
}
